import React, { useState, useEffect, useMemo, useRef } from 'react';
import './PlayerStats.css';
import { FiAlertCircle } from 'react-icons/fi';
import { useApi } from './useApi';
import { useAuth } from './AuthContext';
import { buildVerdict, hasVerdict } from './utils/gameVerdict';
import { buildScoringVerdict } from './utils/scoringVerdict';
import {
  parBreakdownFrom, scoringTotalsFrom, formPointsFrom, isCompleteCard,
} from './utils/insightsScoring';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, ReferenceLine, Tooltip,
  CartesianGrid
} from 'recharts';

// ─── Design Tokens ───
const TOKENS = {
  gold: '#D4AF37',
  teal: '#00E5FF',
  green: '#00E676',
  red: '#FF5252',
  panelBg: 'rgba(15, 23, 42, 0.95)',
  textPrimary: 'rgba(255,255,255,0.95)',
  textSecondary: 'rgba(255,255,255,0.5)',
};

// ─── Animated Number ───
const AnimatedNumber = ({ value, decimals = 0, prefix = '', suffix = '' }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (value == null) return;
    const duration = 800;
    const start = performance.now();
    const from = 0;
    const to = value;

    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);

  if (value == null) return <span>-</span>;
  return <span>{prefix}{display.toFixed(decimals)}{suffix}</span>;
};

// ─── Custom Tooltip ───
const ChartTooltip = ({ active, payload, label, valueLabel }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="broadcast-tooltip">
      <div className="broadcast-tooltip-label">{label}</div>
      <div className="broadcast-tooltip-value">{valueLabel || ''}{payload[0].value}</div>
    </div>
  );
};

// ─── Score color helper ───
const getScoreClass = (val) => {
  if (val == null) return '';
  if (val <= -1) return 'broadcast-green';
  if (val <= 0.3) return 'broadcast-teal';
  if (val <= 1) return 'broadcast-gold';
  return 'broadcast-red';
};

// ─── Enhanced Stats Computation ───
const computePlayerStats = (rounds, currentIndex = null) => {
  // Complete cards only (utils/insightsScoring): a conceded match or a nine
  // has a total that describes golf nobody played.
  const finalized = rounds
    .filter(r => r.is_finalized && r.stableford_total != null && isCompleteCard(r))
    .sort((a, b) => (a.date_played || '').localeCompare(b.date_played || ''));

  if (finalized.length === 0) return null;

  const recent = finalized.slice(-5);
  const recentScores = recent.map(r => r.stableford_total);
  const recentAvg = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
  const seasonAvg = finalized.reduce((a, r) => a + r.stableford_total, 0) / finalized.length;

  const bestSf = finalized.reduce((best, r) =>
    (!best || r.stableford_total > best.stableford_total) ? r : best, null);
  const worstSf = finalized.reduce((worst, r) =>
    (!worst || r.stableford_total < worst.stableford_total) ? r : worst, null);

  const bestGross = finalized.filter(r => r.gross_score_total != null)
    .reduce((best, r) => (!best || r.gross_score_total < best.gross_score_total) ? r : best, null);

  let best9 = null;
  finalized.forEach(r => {
    if (r.stableford_front_9 != null && (!best9 || r.stableford_front_9 > best9.value))
      best9 = { value: r.stableford_front_9, side: 'Front 9', round: r };
    if (r.stableford_back_9 != null && (!best9 || r.stableford_back_9 > best9.value))
      best9 = { value: r.stableford_back_9, side: 'Back 9', round: r };
  });

  let frontTotal = 0, frontCount = 0, backTotal = 0, backCount = 0;
  finalized.forEach(r => {
    if (r.stableford_front_9 != null) { frontTotal += r.stableford_front_9; frontCount++; }
    if (r.stableford_back_9 != null) { backTotal += r.stableford_back_9; backCount++; }
  });
  const frontAvg = frontCount > 0 ? frontTotal / frontCount : null;
  const backAvg = backCount > 0 ? backTotal / backCount : null;

  /* Handicap trend.
   *
   * `player_handicap_index` is the index a round was STARTED on — the snapshot
   * `end_round` adds the adjustment to — so a series built from it alone stops
   * one round short: the last point is the index from BEFORE the most recent
   * round's cut, and the badge above the chart called that "current". After a
   * round that cut two shots the profile read 9.4 while this panel still said
   * 11.4, and the drop never appeared on the line at all.
   *
   * The player's live index closes the series. Together the points are then
   * every index the player has actually held: the start of R1, the start of R2
   * (which IS R1's result), … and finally where they stand now. */
  const handicapData = finalized
    .filter(r => r.player_handicap_index != null)
    .map((r, i) => ({
      round: `R${i + 1}`,
      index: parseFloat(r.player_handicap_index.toFixed(1)),
      date: r.date_played,
    }));
  if (currentIndex != null && !Number.isNaN(currentIndex) && handicapData.length > 0) {
    handicapData.push({
      round: 'Now',
      index: parseFloat(Number(currentIndex).toFixed(1)),
      date: null,
    });
  }

  // Score history for trend chart
  const scoreHistory = finalized.map((r, i) => ({
    round: `R${i + 1}`,
    score: r.stableford_total,
    gross: r.gross_score_total,
    course: r.course?.name || '',
    date: r.date_played,
  }));

  // By par and the scoring mix are NOT counted here any more — they come from
  // the server's summary, the one My Performance reads (utils/insightsScoring).

  // Recent rounds table data
  const recentRounds = finalized.slice(-5).reverse().map(r => ({
    date: r.date_played,
    course: r.course?.name || '-',
    stableford: r.stableford_total,
    gross: r.gross_score_total,
    handicap: r.player_handicap_index != null ? r.player_handicap_index.toFixed(1) : '-',
    front9: r.stableford_front_9,
    back9: r.stableford_back_9,
  }));

  return {
    recentScores,
    recentAvg: Math.round(recentAvg * 10) / 10,
    seasonAvg: Math.round(seasonAvg * 10) / 10,
    bestSf, worstSf, bestGross, best9,
    frontAvg: frontAvg != null ? Math.round(frontAvg * 10) / 10 : null,
    backAvg: backAvg != null ? Math.round(backAvg * 10) / 10 : null,
    handicapData, scoreHistory, recentRounds,
    roundCount: finalized.length,
    currentHandicap: handicapData.length > 0 ? handicapData[handicapData.length - 1].index : null,
    lowestHandicap: handicapData.length > 0 ? Math.min(...handicapData.map(d => d.index)) : null,
  };
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : dateStr;
};

// ─── Course Hole Performance ───
// ─── Main Component ───
const PlayerStats = () => {
  const { authenticatedFetch } = useApi();
  const { currentSociety } = useAuth();
  const [players, setPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(false);
  // The server's summary (same as My Performance), and — for your OWN
  // player only — your shot stats, so the verdict here is the one there.
  const [scoring, setScoring] = useState(null);
  const [shots, setShots] = useState(null);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        const res = await authenticatedFetch('/players');
        if (res.ok) {
          const raw = await res.json();
          const data = [...raw].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setPlayers(data);
          const myId = currentSociety?.player_id;
          if (myId && data.some(p => String(p.id) === String(myId))) {
            setSelectedPlayerId(myId);
          } else if (data.length > 0) {
            setSelectedPlayerId(data[0].id);
          }
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchPlayers();
  }, [authenticatedFetch, currentSociety]);

  useEffect(() => {
    if (!selectedPlayerId) { setRounds([]); return; }
    const fetchRounds = async () => {
      try {
        setLoading(true);
        const res = await authenticatedFetch(`/rounds?player_id=${selectedPlayerId}`);
        if (res.ok) setRounds(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchRounds();
  }, [selectedPlayerId, authenticatedFetch]);

  const isMe = !!selectedPlayerId && String(selectedPlayerId) === String(currentSociety?.player_id);
  useEffect(() => {
    setScoring(null);
    setShots(null);
    if (!selectedPlayerId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(`/players/${selectedPlayerId}/scoring-stats`);
        if (res.ok && !cancelled) setScoring(await res.json());
      } catch { /* the panels fall back to their empty states */ }
      if (!isMe) return;
      try {
        // 403 without the stats plan — then the scorecard verdict stands.
        const res = await authenticatedFetch('/me/shot-stats');
        if (res.ok && !cancelled) setShots(await res.json());
      } catch { /* no shot verdict */ }
    })();
    return () => { cancelled = true; };
  }, [selectedPlayerId, isMe, authenticatedFetch]);

  // The same conclusion My Performance opens with (components/GameVerdict).
  const verdict = useMemo(() => {
    const fromShots = buildVerdict({ shots, scoring });
    if (hasVerdict(fromShots)) return fromShots;
    const fromScores = buildScoringVerdict(scoring);
    return hasVerdict(fromScores) ? fromScores : null;
  }, [shots, scoring]);
  const parBreakdown = useMemo(() => parBreakdownFrom(scoring), [scoring]);
  const scoringTotals = useMemo(() => scoringTotalsFrom(scoring), [scoring]);
  const formPoints = useMemo(() => formPointsFrom(scoring), [scoring]);

  const selectedPlayer = players.find(p => String(p.id) === String(selectedPlayerId));
  // The player's live index closes the handicap series — see computePlayerStats.
  const stats = useMemo(
    () => computePlayerStats(rounds, selectedPlayer?.handicap ?? null),
    [rounds, selectedPlayer?.handicap]);
  // Current Form is the cards My Performance's "Last N rounds" shows, in
  // points. Before the summary arrives (or on an older server) it is this
  // screen's own last five complete cards.
  const form = formPoints.length ? formPoints : (stats?.recentScores || []);
  const formAvg = form.length ? Math.round((form.reduce((a, b) => a + b, 0) / form.length) * 10) / 10 : null;


  return (
    <div className="broadcast-container">
      {/* Player Selector */}
      <div className="broadcast-selector-wrapper">
        <select value={selectedPlayerId} onChange={e => setSelectedPlayerId(e.target.value)} className="broadcast-dropdown">
          <option value="">Select Player</option>
          {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="broadcast-empty"><p>Loading...</p></div>
      ) : !stats ? (
        <div className="broadcast-empty">
          <FiAlertCircle size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
          <p>{selectedPlayerId ? 'No finalized rounds found.' : 'Select a player to view insights.'}</p>
        </div>
      ) : (
        <div className="broadcast-grid">

          {/* ── HERO STATS STRIP ── */}
          <div className="broadcast-panel hero-strip">
            <div className="hero-player-name">{selectedPlayer?.name || 'Player'}</div>
            <div className="hero-stats-row">
              <div className="hero-stat">
                <span className="hero-stat-value gold"><AnimatedNumber value={stats.roundCount} /></span>
                <span className="hero-stat-label">Rounds</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-value"><AnimatedNumber value={stats.seasonAvg} decimals={1} /></span>
                <span className="hero-stat-label">Avg Pts</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-value teal"><AnimatedNumber value={stats.bestSf?.stableford_total} /></span>
                <span className="hero-stat-label">Best</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-value"><AnimatedNumber value={stats.currentHandicap} decimals={1} /></span>
                <span className="hero-stat-label">HCP</span>
              </div>
            </div>
          </div>

          {/* ── THE VERDICT — My Performance's own, off the same figures ── */}
          {verdict && (
            <div className="broadcast-panel ps-verdict">
              <div className="panel-header">
                <span className="panel-title">{isMe ? 'Where your game is' : 'Where their game is'}</span>
              </div>
              <p className="ps-verdict-headline">{verdict.headline}</p>
              {verdict.lines.map(l => <p key={l.key} className="ps-verdict-line">{l.text}</p>)}
              {verdict.focus && (
                <p className="ps-verdict-line">
                  <b>Fix first · {verdict.focus.label}.</b> {verdict.focus.fact}
                </p>
              )}
              {verdict.caveat && <p className="ps-verdict-caveat">{verdict.caveat}</p>}
            </div>
          )}

          {/* ── FORM: LAST 5 ── */}
          <div className="broadcast-panel">
            <div className="panel-header">
              <span className="panel-title">Current Form</span>
              <span className="panel-subtitle">Last {form.length} rounds</span>
            </div>
            <div className="form-pills-row">
              {form.map((s, i) => (
                <div key={i} className={`form-pill ${s >= 36 ? 'form-pill-good' : s >= 30 ? 'form-pill-avg' : 'form-pill-poor'}`}>
                  {s}
                </div>
              ))}
            </div>
            <div className="form-averages">
              <div className="form-avg-block">
                <span className="form-avg-num"><AnimatedNumber value={formAvg} decimals={1} /></span>
                <span className="form-avg-lbl">Recent Avg</span>
              </div>
              <div className="form-avg-block">
                <span className="form-avg-num"><AnimatedNumber value={stats.seasonAvg} decimals={1} /></span>
                <span className="form-avg-lbl">Season Avg</span>
              </div>
              <div className="form-avg-block">
                <span className="form-avg-num">{formAvg > stats.seasonAvg ? '+' : ''}<AnimatedNumber value={formAvg == null ? null : formAvg - stats.seasonAvg} decimals={1} /></span>
                <span className="form-avg-lbl">vs Season</span>
              </div>
            </div>
          </div>

          {/* ── SCORE TREND CHART ── */}
          <div className="broadcast-panel">
            <div className="panel-header">
              <span className="panel-title">Score Trend</span>
              <span className="panel-subtitle">Stableford points per round</span>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={stats.scoreHistory} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={TOKENS.teal} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={TOKENS.teal} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="round" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} domain={['dataMin - 3', 'dataMax + 3']} />
                  <ReferenceLine y={stats.seasonAvg} stroke={TOKENS.gold} strokeDasharray="6 4" strokeWidth={1.5} label={{ value: `Avg ${stats.seasonAvg}`, fill: TOKENS.gold, fontSize: 10, position: 'right' }} />
                  <Tooltip content={<ChartTooltip valueLabel="Pts: " />} />
                  <Area type="monotone" dataKey="score" stroke={TOKENS.teal} strokeWidth={2.5} fill="url(#scoreFill)" dot={{ r: 4, fill: TOKENS.teal, strokeWidth: 0 }} activeDot={{ r: 6, fill: TOKENS.teal, stroke: '#fff', strokeWidth: 2 }} animationDuration={800} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── HANDICAP TREND ── */}
          {stats.handicapData.length >= 2 && (
            <div className="broadcast-panel">
              <div className="panel-header">
                <span className="panel-title">Handicap Trend</span>
                <span className="panel-badge">{stats.currentHandicap} <span className="badge-sub">current</span></span>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={stats.handicapData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="hcpFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={TOKENS.gold} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={TOKENS.gold} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="round" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} domain={['dataMin - 1', 'dataMax + 1']} />
                    <Tooltip content={<ChartTooltip valueLabel="HCP: " />} />
                    <Area type="monotone" dataKey="index" stroke={TOKENS.gold} strokeWidth={2.5} fill="url(#hcpFill)" dot={{ r: 3, fill: TOKENS.gold, strokeWidth: 0 }} activeDot={{ r: 6, fill: TOKENS.gold, stroke: '#fff', strokeWidth: 2 }} animationDuration={800} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {stats.lowestHandicap != null && (
                <div className="hcp-lowest">Lowest: <span className="hcp-lowest-val">{stats.lowestHandicap}</span></div>
              )}
            </div>
          )}

          {/* ── PAR PERFORMANCE ── */}
          <div className="broadcast-panel">
            <div className="panel-header">
              <span className="panel-title">Performance by Par</span>
              <span className="panel-subtitle">Avg strokes vs par · last {scoring?.rounds || 0} rounds</span>
            </div>
            {parBreakdown.every(p => p.avg === null) ? (
              <div className="broadcast-empty" style={{ border: 'none', padding: '20px' }}>
                <p>Not enough hole data yet</p>
              </div>
            ) : (
              <div className="par-perf-grid">
                {parBreakdown.map(p => {
                  if (p.avg === null) return null;
                  const rounded = Math.round(p.avg * 100) / 100;
                  const display = rounded >= 0 ? `+${rounded.toFixed(2)}` : rounded.toFixed(2);
                  const colorClass = getScoreClass(rounded);
                  const total = p.birdies + p.pars + p.bogeys + p.doubles + p.eagles;
                  return (
                    <div key={p.par} className="par-perf-card">
                      <div className="par-perf-label">Par {p.par}</div>
                      <div className={`par-perf-avg ${colorClass}`}>{display}</div>
                      <div className="par-perf-holes">{p.count} holes</div>
                      <div className="par-perf-breakdown">
                        {p.eagles > 0 && <div className="par-segment">
                          <span className="par-seg-dot eagle" />
                          <span className="par-seg-count">{p.eagles}</span>
                          <span className="par-seg-label">Eagle</span>
                        </div>}
                        <div className="par-segment">
                          <span className="par-seg-dot birdie" />
                          <span className="par-seg-count">{p.birdies}</span>
                          <span className="par-seg-label">Birdie</span>
                        </div>
                        <div className="par-segment">
                          <span className="par-seg-dot par" />
                          <span className="par-seg-count">{p.pars}</span>
                          <span className="par-seg-label">Par</span>
                        </div>
                        <div className="par-segment">
                          <span className="par-seg-dot bogey" />
                          <span className="par-seg-count">{p.bogeys}</span>
                          <span className="par-seg-label">Bogey</span>
                        </div>
                        {p.doubles > 0 && <div className="par-segment">
                          <span className="par-seg-dot double" />
                          <span className="par-seg-count">{p.doubles}</span>
                          <span className="par-seg-label">Dbl+</span>
                        </div>}
                      </div>
                      {/* Stacked bar */}
                      {total > 0 && (
                        <div className="par-stacked-bar">
                          {p.eagles > 0 && <div className="stack-seg eagle" style={{ width: `${(p.eagles / total) * 100}%` }} />}
                          <div className="stack-seg birdie" style={{ width: `${(p.birdies / total) * 100}%` }} />
                          <div className="stack-seg par" style={{ width: `${(p.pars / total) * 100}%` }} />
                          <div className="stack-seg bogey" style={{ width: `${(p.bogeys / total) * 100}%` }} />
                          {p.doubles > 0 && <div className="stack-seg double" style={{ width: `${(p.doubles / total) * 100}%` }} />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── SCORING DISTRIBUTION ── */}
          <div className="broadcast-panel">
            <div className="panel-header">
              <span className="panel-title">Scoring Distribution</span>
              <span className="panel-subtitle">Last {scoring?.rounds || 0} rounds</span>
            </div>
            <div className="scoring-dist-row">
              {scoringTotals.totalEagles > 0 && (
                <div className="dist-item">
                  <span className="dist-count eagle-text"><AnimatedNumber value={scoringTotals.totalEagles} /></span>
                  <span className="dist-label">Eagles</span>
                </div>
              )}
              <div className="dist-item">
                <span className="dist-count birdie-text"><AnimatedNumber value={scoringTotals.totalBirdies} /></span>
                <span className="dist-label">Birdies</span>
              </div>
              <div className="dist-item">
                <span className="dist-count par-text"><AnimatedNumber value={scoringTotals.totalPars} /></span>
                <span className="dist-label">Pars</span>
              </div>
              <div className="dist-item">
                <span className="dist-count bogey-text"><AnimatedNumber value={scoringTotals.totalBogeys} /></span>
                <span className="dist-label">Bogeys</span>
              </div>
              <div className="dist-item">
                <span className="dist-count double-text"><AnimatedNumber value={scoringTotals.totalDoubles} /></span>
                <span className="dist-label">Dbl+</span>
              </div>
              {scoringTotals.totalBlobs != null && (
                <div className="dist-item">
                  <span className="dist-count blob-text"><AnimatedNumber value={scoringTotals.totalBlobs} /></span>
                  <span className="dist-label">Blobs</span>
                </div>
              )}
            </div>
          </div>

          {/* ── RECORDS ── */}
          <div className="broadcast-panel">
            <div className="panel-header">
              <span className="panel-title">Records</span>
            </div>
            <div className="records-grid">
              <div className="record-item">
                <span className="record-val teal">{stats.bestSf?.stableford_total ?? '-'}</span>
                <span className="record-lbl">Best Stableford</span>
                {stats.bestSf && <span className="record-sub">{stats.bestSf.course?.name} &middot; {formatShortDate(stats.bestSf.date_played)}</span>}
              </div>
              <div className="record-item">
                <span className="record-val">{stats.bestGross?.gross_score_total ?? '-'}</span>
                <span className="record-lbl">Best Gross</span>
                {stats.bestGross && <span className="record-sub">{stats.bestGross.course?.name} &middot; {formatShortDate(stats.bestGross.date_played)}</span>}
              </div>
              <div className="record-item">
                <span className="record-val gold">{stats.best9?.value ?? '-'}</span>
                <span className="record-lbl">Best 9 Holes</span>
                {stats.best9 && <span className="record-sub">{stats.best9.side} &middot; {stats.best9.round.course?.name}</span>}
              </div>
              <div className="record-item">
                <span className="record-val">
                  {stats.frontAvg != null && stats.backAvg != null
                    ? (stats.frontAvg >= stats.backAvg ? 'Front 9' : 'Back 9')
                    : '-'}
                </span>
                <span className="record-lbl">Stronger Side</span>
                {stats.frontAvg != null && stats.backAvg != null && (
                  <span className="record-sub">F: {stats.frontAvg} vs B: {stats.backAvg}</span>
                )}
              </div>
            </div>
          </div>

          {/* ── RECENT ROUNDS TABLE ── */}
          <div className="broadcast-panel">
            <div className="panel-header">
              <span className="panel-title">Recent Rounds</span>
              <span className="panel-subtitle">Last {stats.recentRounds.length}</span>
            </div>
            <div className="broadcast-table-wrap">
              <table className="broadcast-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Course</th>
                    <th>Pts</th>
                    <th>Gross</th>
                    <th>HCP</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentRounds.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'row-even' : ''}>
                      <td className="table-date">{formatShortDate(r.date)}</td>
                      <td className="table-course">{r.course}</td>
                      <td className="table-pts">{r.stableford}</td>
                      <td className="table-gross">{r.gross || '-'}</td>
                      <td className="table-hcp">{r.handicap}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default PlayerStats;
